var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, FormHelperText, Grid, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import { api, ConfirmDialog, useUploadFiles } from '@sierralabs/core-ui';
import { useDataSourceProps } from '../hooks/form/useDataSourceProps';
import { useFormContext } from '../FormContext';
const StyledGrid = styled(Grid) `
  display: flex;
  flex-direction: column;
  .multiple-files-container {
    padding: 0 20px;
    button {
      margin-right: 10px;
    }
  }
`;
const StyledDragArea = styled.div.attrs(props => ({
    color: props.color || 'white'
})) `
  background-color: ${props => props.color};
  position: relative;
  border: solid 1px #ccc;
  border-radius: 4px;
  &:hover {
    .overlay-bar {
      opacity: 1;
    }
  }
  .drag-empty-container {
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .drag-text,
  .drag-button {
    margin: 10px 0;
  }
`;
const StyledTableCell = styled(TableCell) `
  width: 5px;
`;
/**
 * Add the `file.name` to `file.url`.
 *
 * e.g. `/forms/file/Template.docx`
 * @param file The FormFileValue.
 */
export function getFormFileUrl(file) {
    const match = '/forms/file';
    const urlStr = file.url || '';
    if (!file.name || !urlStr.includes(match)) {
        return urlStr;
    }
    // create url using configured baseUrl pathname instead
    // e.g. baseUrl may include /api/v2 pathname
    const baseUrl = api.getBaseUrl();
    const pathname = new URL(baseUrl).pathname + match.slice(1);
    const url = new URL(urlStr, baseUrl);
    url.pathname = `${pathname}/${file.name}`;
    return url.toString();
}
export const FormFile = (_a) => {
    var { element, label, grid, uploadPath = 'form-files', presignEndPoint = 'forms/presign', disabled, edit } = _a, props = __rest(_a, ["element", "label", "grid", "uploadPath", "presignEndPoint", "disabled", "edit"]);
    if (!grid) {
        // default field to expand entire width of form
        grid = { xs: 12 };
    }
    // prioritize element config and fall back to props
    const { multiple = props.multiple, inputType = props.inputType } = element.config || {};
    const { formValues, setFormValue } = useFormContext();
    const key = element.dataSource || element.key;
    const sourceValue = (props.value || formValues[key]);
    const [value, setValue] = useState(() => sourceValue || []);
    const fileInputRef = useRef(null);
    const [dragover, setDragover] = useState(false);
    const [openFileTypeConfirmDialog, setFileTypeConfirmDialog] = useState(false);
    const [attemptedFileName, setAttemptedFileName] = useState('');
    const dataSourceProps = useDataSourceProps(element, value);
    const { uploadFiles, error, upload, remove } = useUploadFiles(uploadPath, presignEndPoint);
    // update value when sourceValue changes / gets loaded
    useEffect(() => {
        sourceValue && setValue(sourceValue);
    }, [sourceValue]);
    // update progress and submit when completed
    useEffect(() => {
        if (uploadFiles) {
            console.log('uploadFiles');
            const newFileValues = uploadFiles.map(uploadFile => {
                var _a;
                return ({
                    url: (_a = uploadFile.presignedResult) === null || _a === void 0 ? void 0 : _a.destinationUrl,
                    name: uploadFile.file.name,
                    size: uploadFile.file.size
                });
            });
            setValue(values => {
                newFileValues.push(
                // remove any duplicate files
                ...values.filter(value => !newFileValues.some(newFile => value.name === newFile.name)));
                return newFileValues;
            });
            const completedFiles = uploadFiles.filter(uploadFile => uploadFile.isCompleted);
            if (uploadFiles.length > 0 &&
                completedFiles.length === uploadFiles.length) {
                // submit once all files are uploaded
                setFormValue(key, newFileValues);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadFiles]);
    // start uploading file(s)
    const handleFile = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (!files || files.length === 0)
            return;
        if (!multiple && uploadFiles && uploadFiles.length > 0) {
            // if only one file allowed, remove it before uploading new file
            remove(uploadFiles[0].file);
        }
        const filesToUpload = [];
        // check if file extension is an accepted input type
        if (!inputType || inputType === '*') {
            filesToUpload.push(...files);
        }
        else {
            for (let i = 0; i < files.length; i++) {
                const fileExtension = files[i].name.match(/\.([^\.]+)$/g);
                const acceptableFileExtension = inputType === null || inputType === void 0 ? void 0 : inputType.includes(fileExtension ? fileExtension[0] : ' ');
                if (acceptableFileExtension) {
                    // if the file extension is acceptable, push to the filesToUpload array
                    filesToUpload.push(files[i]);
                }
                else {
                    // if not acceptable, alert the user
                    setAttemptedFileName(files[i].name);
                    setFileTypeConfirmDialog(true);
                }
            }
        }
        // if the filesToUpload array has at least one file, proceed to upload
        if (filesToUpload.length > 0) {
            upload(multiple ? filesToUpload : [filesToUpload[0]]);
        }
    });
    const formatFileSize = (value) => {
        return Math.round((value / 1024) * 100).toLocaleString();
    };
    // disable when editing
    if (!disabled)
        disabled = edit;
    return (React.createElement(StyledGrid, Object.assign({ item: true, className: 'inline-text-field' }, grid),
        label && (React.createElement(Typography, { variant: 'h5', gutterBottom: true }, label)),
        React.createElement(StyledDragArea, { color: dragover ? '#d3d3d3' : 'white', onDragOver: event => {
                event.preventDefault();
                if (disabled)
                    return;
                setDragover(true);
            }, onDragLeave: () => !disabled && setDragover(false), onDrop: event => {
                event.preventDefault();
                if (disabled)
                    return;
                setDragover(false);
                handleFile(event.dataTransfer.files);
            } },
            !dragover && (value === null || value === void 0 ? void 0 : value.length) > 0 && (React.createElement(TableContainer, null,
                React.createElement(Table, null,
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(StyledTableCell, null),
                            React.createElement(TableCell, null, "File Name"),
                            React.createElement(TableCell, null, "Size"),
                            React.createElement(TableCell, null, "Download"))),
                    React.createElement(TableBody, null, value &&
                        value.length > 0 &&
                        value.map((file, index) => {
                            const fileUrl = getFormFileUrl(file);
                            const uploadFile = uploadFiles === null || uploadFiles === void 0 ? void 0 : uploadFiles.find(x => x.file.name === file.name);
                            return (React.createElement(TableRow, { key: index },
                                React.createElement(TableCell, null,
                                    React.createElement(IconButton, { size: 'small', "aria-label": 'delete', disabled: disabled, onClick: () => {
                                            // TODO: use ConfirmDialog
                                            if (uploadFile) {
                                                remove(uploadFile.file);
                                            }
                                            setValue(value => {
                                                value = value.filter(f => f !== file);
                                                setFormValue(key, value);
                                                return value;
                                            });
                                        } },
                                        React.createElement(CloseIcon, null))),
                                React.createElement(TableCell, null, (file === null || file === void 0 ? void 0 : file.name) || ''),
                                React.createElement(TableCell, null,
                                    formatFileSize((file === null || file === void 0 ? void 0 : file.size) || 0),
                                    " KB"),
                                React.createElement(TableCell, null,
                                    (uploadFile === null || uploadFile === void 0 ? void 0 : uploadFile.isUploading) && (React.createElement(LinearProgress, { color: 'primary', variant: 'determinate', value: uploadFile.progress * 100 })),
                                    fileUrl && (React.createElement("a", { href: fileUrl, download: true }, "Download")))));
                        }))),
                multiple && (React.createElement("div", { className: 'multiple-files-container' },
                    React.createElement(Button, { className: 'drag-button', variant: 'outlined', color: 'primary', disabled: disabled, onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, startIcon: React.createElement(AddIcon, null) }, "Add File"),
                    React.createElement(Typography, { variant: 'overline' },
                        ' ',
                        "or drag files into the table above."))))),
            (dragover || (value === null || value === void 0 ? void 0 : value.length) === 0) && (React.createElement(Fragment, null,
                React.createElement("div", { className: 'drag-empty-container' },
                    React.createElement(Typography, { variant: 'subtitle2', className: 'drag-text' }, "Drag a file here"),
                    React.createElement(Typography, { variant: 'subtitle2', className: 'or-text' }, "-- or --"),
                    React.createElement(Button, { className: 'drag-button', variant: 'outlined', color: 'primary', disabled: disabled, onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); } }, "Select File")))),
            React.createElement("input", { hidden: true, multiple: multiple, type: 'file', accept: inputType, ref: fileInputRef, onChange: event => handleFile(event === null || event === void 0 ? void 0 : event.target.files) })),
        error && (React.createElement(Box, { mt: 2 },
            React.createElement(Alert, { severity: 'error' }, "Error uploading. Please check the internet connection and try again."))),
        React.createElement(ConfirmDialog, { open: openFileTypeConfirmDialog, title: 'Invalid file extension', message: `The file "${attemptedFileName}" is not an accepted file type. Contact administrator.`, onCancel: () => {
                setFileTypeConfirmDialog(false);
            }, onConfirm: () => {
                setFileTypeConfirmDialog(false);
            } }),
        dataSourceProps.helperText && (React.createElement(FormHelperText, { error: dataSourceProps.error }, dataSourceProps.helperText))));
};
export default FormFile;
//# sourceMappingURL=FormFile.js.map